// Validación de entradas compartida (importes, fechas, textos, cantidades). Pura, sin React.
// Devuelve siempre { ok, valor, error }: el formulario muestra `error` y no guarda si !ok.

export const parseNumero = (raw) => {
  if (raw == null) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  let s = String(raw).trim().replace(/\s|€/g, '');
  if (!s) return null;
  // 1.234,56 → 1234.56 · 1234,56 → 1234.56 · 1234.56 → 1234.56
  if (/,\d{1,2}$/.test(s)) s = s.replace(/\./g, '').replace(',', '.');
  else if (/^-?\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
  else s = s.replace(',', '.');
  if (!/^-?\d*\.?\d+$/.test(s)) return null;
  const x = Number(s);
  return Number.isFinite(x) ? x : null;
};

const fmt = (x) => x.toLocaleString('es-ES', { maximumFractionDigits: 2 });

/** Importe en euros. Por defecto: no negativo, máximo 1.000.000 €, 2 decimales. */
export function validarImporte(raw, { etiqueta = 'El importe', min = 0, max = 1e6, obligatorio = false } = {}) {
  if (raw == null || String(raw).trim() === '') return obligatorio ? { ok: false, error: `${etiqueta} es obligatorio` } : { ok: true, valor: null };
  const x = parseNumero(raw);
  if (x == null) return { ok: false, error: `${etiqueta} debe ser un número (ej. 125,50)` };
  if (x < min) return { ok: false, error: min === 0 ? `${etiqueta} no puede ser negativo` : `${etiqueta} debe ser al menos ${fmt(min)}` };
  if (x > max) return { ok: false, error: `${etiqueta} es demasiado alto (máx. ${fmt(max)} €)` };
  return { ok: true, valor: Math.round(x * 100) / 100 };
}

/** Cantidad entera (unidades). */
export function validarCantidad(raw, { etiqueta = 'La cantidad', min = 1, max = 100000, obligatorio = false } = {}) {
  if (raw == null || String(raw).trim() === '') return obligatorio ? { ok: false, error: `${etiqueta} es obligatoria` } : { ok: true, valor: null };
  const x = parseNumero(raw);
  if (x == null || !Number.isInteger(x)) return { ok: false, error: `${etiqueta} debe ser un número entero` };
  if (x < min || x > max) return { ok: false, error: `${etiqueta} debe estar entre ${min} y ${fmt(max)}` };
  return { ok: true, valor: x };
}

/** Número en un rango (temperaturas, porcentajes…). */
export function validarRango(raw, { etiqueta = 'El valor', min, max, obligatorio = true, unidad = '' } = {}) {
  if (raw == null || String(raw).trim() === '') return obligatorio ? { ok: false, error: `${etiqueta} es obligatorio` } : { ok: true, valor: null };
  const x = parseNumero(raw);
  if (x == null) return { ok: false, error: `${etiqueta} debe ser un número` };
  if (x < min || x > max) return { ok: false, error: `${etiqueta} debe estar entre ${fmt(min)} y ${fmt(max)}${unidad}` };
  return { ok: true, valor: x };
}

const hoyMadrid = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });

/** Fecha AAAA-MM-DD válida. `noFutura`: no después de hoy. `noPasada`: no antes de hoy. */
export function validarFecha(raw, { etiqueta = 'La fecha', noFutura = false, noPasada = false, obligatorio = true, hoy = hoyMadrid() } = {}) {
  const s = String(raw ?? '').trim();
  if (!s) return obligatorio ? { ok: false, error: `${etiqueta} es obligatoria` } : { ok: true, valor: null };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s) || Number.isNaN(Date.parse(`${s}T12:00:00Z`)) || new Date(`${s}T12:00:00Z`).toISOString().slice(0, 10) !== s) return { ok: false, error: `${etiqueta} no es válida` };
  if (s < '2000-01-01' || s > '2100-12-31') return { ok: false, error: `${etiqueta} está fuera de rango` };
  if (noFutura && s > hoy) return { ok: false, error: `${etiqueta} no puede ser futura` };
  if (noPasada && s < hoy) return { ok: false, error: `${etiqueta} no puede ser pasada` };
  return { ok: true, valor: s };
}

/** Mes AAAA-MM. */
export function validarMes(raw, { etiqueta = 'El mes', noPasado = false, hoy = hoyMadrid() } = {}) {
  const s = String(raw ?? '').trim();
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(s)) return { ok: false, error: `${etiqueta} no es válido` };
  if (noPasado && s < hoy.slice(0, 7)) return { ok: false, error: `${etiqueta} ya ha pasado` };
  return { ok: true, valor: s };
}

/** Texto con longitud máxima (recorta espacios). */
export function validarTexto(raw, { etiqueta = 'El texto', max = 500, obligatorio = false } = {}) {
  const s = String(raw ?? '').trim();
  if (!s) return obligatorio ? { ok: false, error: `${etiqueta} es obligatorio` } : { ok: true, valor: '' };
  if (s.length > max) return { ok: false, error: `${etiqueta} es demasiado largo (máx. ${max} caracteres)` };
  return { ok: true, valor: s };
}

/** Teléfono español o internacional: 9–15 dígitos. */
export function validarTelefono(raw, { obligatorio = false } = {}) {
  const s = String(raw ?? '').trim();
  if (!s) return obligatorio ? { ok: false, error: 'El teléfono es obligatorio' } : { ok: true, valor: '' };
  if (!/^[0-9+\-\s().]+$/.test(s)) return { ok: false, error: 'El teléfono solo puede tener números, espacios y +' };
  const d = s.replace(/\D/g, '');
  if (d.length < 9 || d.length > 15) return { ok: false, error: 'Teléfono no válido (9 a 15 cifras)' };
  return { ok: true, valor: s };
}

/** Primer error de una lista de validaciones (o null). */
export const primerError = (...rs) => rs.find((r) => r && !r.ok)?.error || null;
